import { PartnerGeschlecht } from '@mng/domain';
import {
  BooleanField,
  DependencyFunction,
  findObjectForPropertyInModelContext,
  IModelContext,
  isFrageTrue,
  ISimpleValidationResult,
  IValidationResult,
  ModelDependency,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  ModelOption,
  NumberField,
  RequiredFunction,
  requiredValueOptionsValidation,
  StringField,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import { getAlterForGeburtstag, getAlterInMonatenForGeburtstag, isDefined, today } from '@mng/mng-ts-helpers';
import { GvAngebotData, GvPerson } from '../../domain/gvangebot.model.types';
import { StammdatenBase } from '../allgemein/stammdaten-base';
import { createOptionTrueDependencyFunction } from '../antrag/gesundheitsfragen/gv-gesundheitsfragen.dependencies';
import {
  createGvAusgefuehrteGeschaeftsfaelleModelGroup,
  createGvErlaubteGeschaeftsfaelleModelGroup
} from './gvgeschaeftsfall.model';
import { brauchtSchwangerschaftsDeckung, gvPersonenValidation, isBeziehungZuVnReadOnly } from './gvperson.validation';
import { createGvTarifDataModelGroup } from './gvtarif.model';
import {
  getAltersGrenzeFuerAusbildungsFrage,
  isElternteil,
  isVnBeziehungEhegatteMoeglich,
  VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE,
  VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER,
  VN_BEZ_OPTION_VALUE_KIND,
  VN_BEZ_OPTION_VALUE_SONSTIGER
} from './logic/gvperson-model.logic';
import { ITEM_GV_PERSONEN_DATA_PERSONEN_STAMMDATEN, ITEM_MODIFIED_DATE } from '../gvangebot.model.paths';

const HAUPTWOHNSITZ_OESTERREICH_ID = 'hauptwohnsitzOesterreich';

const ACHTZEHN_IN_MONATEN = 18 * 12;
const MAX_MONATE_VOR_ACHTZEHN_FUER_UMSTELLUNG_IN_ERWACHSENENPRAEMIE = 4;

export const VN_BEZIEHUNG_OPTIONS_OHNE_EHEGATTE = [
  { name: 'Kind', value: VN_BEZ_OPTION_VALUE_KIND },
  { name: 'Sonstiger Angehöriger (Enkelkinder, Geschwister, Großeltern, ...)', value: VN_BEZ_OPTION_VALUE_SONSTIGER },
  { name: 'Kein Angehöriger', value: VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER }
];

export const vnBeziehungOptions = [
  { name: 'Ehegatte/Lebensgefährte', value: VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE },
  ...VN_BEZIEHUNG_OPTIONS_OHNE_EHEGATTE
];

export const vnBeziehungValueOptions: ValueOptions = new ValueOptions(vnBeziehungOptions);

export function createBaseGvPersonenDataModelGroup(id: string): ModelGroup {
  return new ModelGroup(id)
    .addItems(new NumberField('gesamtPraemie').withReadOnly())
    .addArrayGroupObject(createGvPersonenModelGroup());
}

export function createGvPersonenDataModelGroup(): ModelGroup {
  return createBaseGvPersonenDataModelGroup('gvPersonenData').withValidation(gvPersonenValidation);
}

export function createGvVertragBestandPersonenDataModelGroup(): ModelGroup {
  return createBaseGvPersonenDataModelGroup('gvVertragBestandPersonenData')
    .withInterfaceName('GvPersonenData')
    .withNoValidation();
}

export function createGvVertrag2BestandPersonenDataModelGroup(): ModelGroup {
  return createBaseGvPersonenDataModelGroup('gvVertrag2BestandPersonenData')
    .withInterfaceName('GvPersonenData')
    .withNoValidation();
}

export function createGvPersonenModelGroup(id = 'personen'): ModelGroup {
  const personModelGroup: ModelGroup = new ModelGroup(id, 'GvPerson').withInterfaceName('GvPerson');

  const stammdaten = new StammdatenBase();
  stammdaten.init(true, true);
  stammdaten.adresse.withInterfaceName('GvAdressModelData');

  const stammdatenModelGroup = stammdaten.createStammdatenModelGroup().withInterfaceName('GvStammdaten');

  personModelGroup
    .addItems(
      new StringField('id'),
      new NumberField('praemiePerson').withReadOnly(),
      stammdatenModelGroup,
      createGvPersonRollenModelGroup(),
      createHauptwohnsitzAttribut(),
      createGvPersonBeziehungenModelGroup(),
      createGvTarifDataModelGroup(),
      new NumberField('sozialVersicherung'),
      new BooleanField('nachversicherungKurzpflegeHauptfaelligkeit').withReadOnly()
    )
    .addArrayGroupObject(
      createGvErlaubteGeschaeftsfaelleModelGroup().withImportDef({
        localImport: true
      })
    )
    .addArrayGroupObject(
      createGvAusgefuehrteGeschaeftsfaelleModelGroup().withImportDef({
        localImport: true
      })
    );

  return personModelGroup;
}

function createGvPersonRollenModelGroup(): ModelGroup {
  const personRollenModelGroup: ModelGroup = new ModelGroup('personRollen', 'Rollen Person')
    .withInterfaceName('GvPersonRollen')
    .addItems(new BooleanField('vn', 'VN').withReadOnly(), new BooleanField('vp', 'VP').withReadOnly());

  return personRollenModelGroup;
}

function createGvPersonBeziehungenModelGroup(): ModelGroup {
  const personBeziehungenModelGroup: ModelGroup = new ModelGroup('personBeziehungen', 'Beziehungen')
    .withInterfaceName('GvPersonBeziehungen')
    .withFunctionDependency(gvPersonNeedsBeziehung, false)
    .addItems(
      new StringField('vnBeziehung', 'Beziehung zum Versicherungsnehmer')
        .withRequired()
        .withRemoveDataWhenNotRelevant()
        .withOptions(
          new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(
            vnBeziehungValueOptions,
            vnBeziehungAuswahlFilter
          )
        )
        .withValidation(vnBeziehungValid)
        .withFunctionDependency(gvPersonNeedsVnBeziehung)
        .withReadOnlyFunction(isBeziehungZuVnReadOnly),
      new NumberField('elternteilPersonId', 'Elternteilauswahl').withReadOnly(),
      new NumberField('elternteilPersonId2', 'Elternteilauswahl 2').withReadOnly(),
      new BooleanField('inAusbildung', 'Befindet sich diese Person derzeit in Ausbildung?')
        .withFunctionDependency(gvPersonNeedsInAusbildung, true)
        .withRemoveDataWhenNotRelevant(),
      createSchwangerschaftsInfoModelGroup()
    );
  return personBeziehungenModelGroup;
}

function createSchwangerschaftsInfoModelGroup(): ModelGroup {
  const schwangerId = 'schwanger';
  return new ModelGroup('schwangerschaftsInfo', 'Schwangerschaft')
    .withInterfaceName('GvSchwangerschaftsInfo')
    .addItems(
      new BooleanField(schwangerId, 'Besteht eine Schwangerschaft?')
        .withRequiredFunction(gvPersonsSchwangerschaftsFrageRequired)
        .withRemoveDataWhenNotRelevant(),
      new BooleanField('deckung', 'Ist eine Deckung für die Schwangerschaft / Entbindung gewünscht?')
        .withRemoveDataWhenNotRelevant()
        .withFunctionDependency(isFrageTrue(schwangerId))
        .withReadOnlyFunction(brauchtSchwangerschaftsDeckung),
      new NumberField('kindsVaterPersonId', 'Wer ist der Kindesvater?')
        .withRemoveDataWhenNotRelevant()
        .withFunctionDependencies([isFrageTrue(schwangerId), createOptionTrueDependencyFunction('deckung')])
        .withRequiredFunction(kindsVaterRequired)
    )
    .withFunctionDependency(gvPersonNeedsSchwangerschaftsFrage, true)
    .withRemoveDataWhenNotRelevant();
}

const kindsVaterRequired: RequiredFunction = (modelContext: IModelContext): boolean => {
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  return isKindsVaterRequired(gvAngebot, gvPerson);
};

const gvPersonNeedsVnBeziehung: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  return isGvPersonVnBeziehungFrageNotwendig(gvAngebot, gvPerson);
};

const gvPersonNeedsBeziehung: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);

  return (
    isDefined(gvPerson.hauptwohnsitzOesterreich) &&
    // bei einer Vertragsänderungmuss der VP den Hauptwohnsitz nicht in Österreich haben
    (gvPerson.hauptwohnsitzOesterreich || (gvAngebot.vertragsAenderung && gvPerson.personRollen.vp)) &&
    (isGvPersonVnBeziehungFrageNotwendig(gvAngebot, gvPerson) ||
      isAusbildungFrageNotwendigForGvPerson(gvPerson, gvAngebot) ||
      isGvPersonSchwangerschaftsFrageNotwendig(gvPerson, gvAngebot) ||
      isElternteil(gvAngebot, gvPerson))
  );
};

const gvPersonNeedsInAusbildung: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  return isAusbildungFrageNotwendigForGvPerson(gvPerson, gvAngebot);
};

const gvPersonNeedsSchwangerschaftsFrage: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  return isGvPersonSchwangerschaftsFrageNotwendig(gvPerson, gvAngebot);
};

const gvPersonsSchwangerschaftsFrageRequired: RequiredFunction = (modelContext: IModelContext): boolean => {
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  return isGvPersonSchwangerschaftsFrageNotwendig(gvPerson, gvAngebot);
};

export function isGvPersonSchwangerschaftsFrageNotwendig(gvPerson: GvPerson, gvAngebotData: GvAngebotData): boolean {
  if (!gvPerson.personBeziehungen) {
    return false;
  }
  const isPersonWeiblich = gvPerson.stammdaten && PartnerGeschlecht.W === gvPerson.stammdaten.geschlecht;
  if (gvPerson.personRollen.vp && isPersonWeiblich) {
    const alterInMonaten = getAlterInMonatenForGeburtstag(gvPerson.stammdaten.gebdatum);
    const minimalAlter =
      gvAngebotData.vertragsAenderung && gvPerson.personBeziehungen.inAusbildung === false
        ? ACHTZEHN_IN_MONATEN - MAX_MONATE_VOR_ACHTZEHN_FUER_UMSTELLUNG_IN_ERWACHSENENPRAEMIE
        : ACHTZEHN_IN_MONATEN;

    return alterInMonaten >= minimalAlter;
  }
  return false;
}

export function getPotentielleKindsVaeter(gvAngebotData: GvAngebotData, gvPerson: GvPerson): GvPerson[] {
  if (isGvPersonSchwangerschaftsFrageNotwendig(gvPerson, gvAngebotData)) {
    return gvAngebotData.gvPersonenData.personen.filter((current: GvPerson) => {
      return (
        current.personRollen.vp &&
        PartnerGeschlecht.M === current.stammdaten.geschlecht &&
        getAlterForGeburtstag(current.stammdaten.gebdatum) > 14 &&
        gvPerson.personBeziehungen?.elternteilPersonId?.toString() !== current.id &&
        gvPerson.personBeziehungen?.elternteilPersonId2?.toString() !== current.id
      );
    });
  } else {
    return [];
  }
}

export function isAusbildungFrageNotwendigForGvPerson(
  gvPerson: GvPerson,
  gvAngebot: GvAngebotData,
  now = today()
): boolean {
  const alterInMonaten = getAlterInMonatenForGeburtstag(gvPerson.stammdaten.gebdatum, now);

  const obereAltersgrenzeInMonaten =
    getAltersGrenzeFuerAusbildungsFrage(
      gvPerson.stammdaten.sozversanstalt,
      gvAngebot.fzKonto?.konto,
      gvAngebot.vertragsAenderung
    ) *
      12 +
    11;

  const minimalAlter = gvAngebot.vertragsAenderung
    ? ACHTZEHN_IN_MONATEN - MAX_MONATE_VOR_ACHTZEHN_FUER_UMSTELLUNG_IN_ERWACHSENENPRAEMIE
    : ACHTZEHN_IN_MONATEN;

  if (gvAngebot.vertragsAenderung) {
    let bestandsPerson = gvAngebot.gvVertragBestandPersonenData?.personen?.find((p) => p.id === gvPerson.id);
    if (!bestandsPerson) {
      // Aktenzusammenlegung
      bestandsPerson = gvAngebot.gvVertrag2BestandPersonenData?.personen?.find((p) => p.id === gvPerson.id);
    }
    // wenn im alten vertrag keine Kinderprämie hatte -> Frage nicht notwendig
    if (alterInMonaten >= minimalAlter && bestandsPerson && bestandsPerson.gvTarifData) {
      return bestandsPerson.gvTarifData.tarife.some((t) => t.versPraemArt === 'K');
    }
  }

  return gvPerson.personBeziehungen && alterInMonaten >= minimalAlter && alterInMonaten <= obereAltersgrenzeInMonaten;
}

function isGvPersonVnBeziehungFrageNotwendig(gvAngebot: GvAngebotData, gvPerson: GvPerson): boolean {
  if (gvAngebot) {
    const vn: GvPerson = gvAngebot.gvPersonenData.personen.find((person) => person.personRollen.vn);
    if (vn) {
      return !vn.stammdaten.juristischePerson && !isGvPersonVn(gvPerson) && isGvPersonVp(gvPerson);
    }
  } else {
    return false;
  }
}

function isKindsVaterRequired(gvAngebot: GvAngebotData, gvPerson: GvPerson): boolean {
  if (gvAngebot) {
    // TODO: 17 + 9 ??????
    if (
      getAlterForGeburtstag(gvPerson.stammdaten.gebdatum) >= 18 &&
      gvPerson.personBeziehungen?.schwangerschaftsInfo?.schwanger &&
      gvPerson.personBeziehungen?.schwangerschaftsInfo?.deckung
    ) {
      const bestandsPerson: GvPerson = gvAngebot.gvVertragBestandPersonenData?.personen.find(
        (person) => person.id === gvPerson.id
      );
      if (
        bestandsPerson &&
        bestandsPerson.gvTarifData &&
        bestandsPerson.gvTarifData.tarife.some((t) => t.versPraemArt === 'K')
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  } else {
    return false;
  }
}

function isGvPersonVn(gvPerson: GvPerson): boolean {
  return gvPerson.personRollen && gvPerson.personRollen.vn;
}

function isGvPersonVp(gvPerson: GvPerson): boolean {
  return gvPerson.personRollen && gvPerson.personRollen.vp;
}

export function hasGvPersonVnBeziehung(gvPerson: GvPerson) {
  return gvPerson.personBeziehungen && !!gvPerson.personBeziehungen.vnBeziehung;
}

export function vnBeziehungAuswahlFilter(modelContext: IModelContext, baseOptions?: ModelOption[]): ModelOption[] {
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  if (getAlterForGeburtstag(gvPerson.stammdaten.gebdatum) < 16) {
    return VN_BEZIEHUNG_OPTIONS_OHNE_EHEGATTE;
  } else if (!isVnBeziehungEhegatteMoeglich(gvAngebot, gvPerson)) {
    return VN_BEZIEHUNG_OPTIONS_OHNE_EHEGATTE;
  }
  return baseOptions;
}

export const vnBeziehungValid: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  const simpleValidationResult: ISimpleValidationResult = isVNBeziehungValid(modelContext.value, gvPerson, gvAngebot);

  if (simpleValidationResult.valid) {
    return null;
  } else {
    return {
      id: 'vnBeziehung',
      message: simpleValidationResult.message
    };
  }
};

export function isVNBeziehungValid(
  vnBeziehung: string,
  gvPerson: GvPerson,
  gvAngebot: GvAngebotData
): ISimpleValidationResult {
  const result: ISimpleValidationResult = { valid: true };

  const ehegatten = gvAngebot.gvPersonenData.personen.filter(
    (person) =>
      person.personBeziehungen?.vnBeziehung === VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE &&
      (person.personRollen.vn || person.personRollen.vp)
  );
  if (
    ehegatten.length >= 2 &&
    gvPerson.personBeziehungen.vnBeziehung === VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE
  ) {
    result.valid = false;
    result.message = 'Es darf nur einen Ehegatten / Lebensgefährten geben.';
  }
  return result;
}

export const hauptwohnsitzValidation: ValidationFunction = (modelItem: ModelItem, modelContext: IModelContext) => {
  const hautpwohnsitzOesterreich = modelContext.value;
  const gvAngebot: GvAngebotData = getGvAngebotDataFromContext(modelContext);
  const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
  return isHauptwohnsitzValid(gvAngebot, gvPerson, hautpwohnsitzOesterreich);
};

export function isHauptwohnsitzValid(
  gvAngebot: GvAngebotData,
  gvPerson: GvPerson,
  hauptwohnsitzOesterreich: boolean
): IValidationResult {
  // https://jira.merkur.net/browse/SO-1714
  if (gvPerson.personRollen?.vn || gvPerson.personRollen?.vp) {
    // bei Neugeschäft müsssen VN und VP Wohnsitz in Österreich haben
    // bei Vertragsänderung nur der VP
    if (
      !hauptwohnsitzOesterreich &&
      (!gvAngebot.vertragsAenderung || (gvAngebot.vertragsAenderung && gvPerson.personRollen.vn))
    ) {
      return {
        id: HAUPTWOHNSITZ_OESTERREICH_ID,
        message:
          'Für dieses Produkt ist ein Hauptwohnsitz oder der private Lebensmittelpunkt in Österreich erforderlich!'
      } as IValidationResult;
    } else {
      return null;
    }
  }
}

function createHauptwohnsitzAttribut(): BooleanField {
  const desc = 'Haben Sie Ihren Hauptwohnsitz oder privaten Lebensmittelpunkt in Österreich?';

  return new BooleanField(HAUPTWOHNSITZ_OESTERREICH_ID, desc)
    .withFunctionDependency((dependency: ModelDependency, modelContext: IModelContext) => {
      const gvPerson: GvPerson = getGvPersonFromContext(modelContext);
      return gvPerson.personRollen?.vn || gvPerson.personRollen?.vp;
    })
    .withValidation(hauptwohnsitzValidation);
}

function getGvPersonFromContext(modelContext: IModelContext): GvPerson {
  const person: GvPerson = findObjectForPropertyInModelContext(modelContext, ITEM_GV_PERSONEN_DATA_PERSONEN_STAMMDATEN);

  if (!person) {
    throw new Error('GV Person nicht gefunden');
  }
  return person;
}

function getGvAngebotDataFromContext(modelContext: IModelContext): GvAngebotData {
  const angebot: GvAngebotData = findObjectForPropertyInModelContext(modelContext, ITEM_MODIFIED_DATE);

  if (!angebot) {
    throw new Error('GV AngebotData nicht gefunden');
  }
  return angebot;
}
